import React, { useEffect, useMemo, useState } from "react";

import AttributeErrors from "./attribute-errors";
import { SourcePage } from "shared/taskTypeMap";
import BulkCheck from "./bulk-check";
import Thumbnail from "../../image-stream/thumbnail";
import NotificationBar from "../../notification-bar";
import TaskMessages from "../../notification-bar/task-messages";
import NoteDisplay from "../../notes/components/notes-display";
import Notes from "../../notes/contexts/notes";
import { ToggleSwitch } from "../../form-inputs/inputs";
import { updateShipmentProperty } from "../lib/schedulingData";
import { sampleProductText } from "shared/digital-content";
import { DigitalLiteSwitch } from "../../digital-lite-switch/digital-lite-switch";
import { SHIPMENT_TYPE_MAP } from "../../shipments/lib/constants";
import { ProfilingMainAttributes } from "./profiling-main-attributes";
import { useAttributeGroups } from "shared/hooks/api/use-attribute-groups";
import { cesFeatureGuids } from "../../../contexts/settings/lib/constants";
import { BRAND_VERIFIED_WORKFLOW_TYPE_ID, BRAND_VERIFIED_LOCATION } from "components/shipment-editor/lib/constants";

const Product = ({
    onValidateGroupAttributes,
    invalidGroupAttributes,
    taskType,
    missingAttributesList,
    setMissingAttributesList,
    settings,
    ...props
}) => {
    const {
        binTrackingNumber,
        codingWipKey,
        cxHClientCode,
        isSampleUpc,
        isFinalized,
        matchedProductTaskContextKey,
        productMatchType,
        messaging,
        photographyWipKey,
        productModifier,
        shipmentKey,
        shipmentProductKey,
        shipmentType,
        upc,
        shipmentProductFeatures,
        profilingTaskKey,
    } = props.upc;
    const [hasPremiumEditing, setHasPremiumEditing] = useState(props.upc.hasPremiumEditing);
    const itemInfo = props.taskInfo.filter((x) => x.taskContextKey === props.upc.key)[0];
    const includeBulk = props.hasBulk && !isFinalized && !props.isCompleted && itemInfo?.state !== "completed";
    const [isProductInvalid, setProductInvalidStatus] = useState(true);
    const [hasMissingSizeAttributes, showMissingSizeAttributes] = useState(false);
    const { data: definitions, refetch: refetchDefinitions } = useAttributeGroups(["IntakeProfilingNew"], {
        codingWipKey: codingWipKey,
        taskType: "scheduling.default",
    });
    const isCostcoConfigActive = settings.intakeConfigFeatures[cesFeatureGuids.COSTCOSPSUP]?.isActive;
    const productFeature = useMemo(
        () => shipmentProductFeatures?.find((product) => product.id === cesFeatureGuids.COSTCOSPSUP),
        [shipmentProductFeatures]
    );
    const isBrandVerified = props.upc.workflowTypeId === BRAND_VERIFIED_WORKFLOW_TYPE_ID;

    useEffect(() => {
        if (hasMissingSizeAttributes) {
            setMissingAttributesList([...missingAttributesList, codingWipKey]);
        } else {
            setMissingAttributesList(missingAttributesList.filter((key) => key !== codingWipKey));
        }
    }, [hasMissingSizeAttributes, codingWipKey]);

    const patchTaskContext = (name, value) => {
        if (name === "hasPremiumEditing") {
            setHasPremiumEditing(value);
        }

        updateShipmentProperty(shipmentProductKey, name, value);
    };

    const duplicateChecked = matchedProductTaskContextKey ? (
        <div className={`memo txt-info m-t-4 m-b-16 ${shipmentType === 1 && !isFinalized ? "m-l-12" : ""}`}>
            <em>Duplicate Match</em>
            {productMatchType === 2 && <em>ed - Exact Match</em>}
        </div>
    ) : null;

    return (
        <div className="upc">
            <div className="attribute-information row">
                <div className="product-info column -span2">
                    <div className="button-group">
                        <h2
                            style={{ margin: 0 }}
                            className="p-b-12 p-l-24 m-l-12 align-items-center flex-wrap"
                        >
                            {shipmentType >= 0 && (
                                <span className="memo">({isBrandVerified ? BRAND_VERIFIED_LOCATION : SHIPMENT_TYPE_MAP[shipmentType].label})</span>
                            )}
                            {duplicateChecked}
                        </h2>
                    </div>
                    <Thumbnail
                        photoKey={photographyWipKey}
                        shipmentKey={shipmentKey}
                        shipmentProductKey={shipmentProductKey}
                        size="medium"
                        disablePopout
                    />
                    <br />
                    {shipmentType === 1 && !isFinalized && !props.isCompleted && (
                        <DigitalLiteSwitch
                            codingWipKey={codingWipKey}
                            upc={upc}
                            saveCodingWipKey={props.saveCodingWipKey}
                        />
                    )}
                    <ToggleSwitch
                        name="hasPremiumEditing"
                        value={hasPremiumEditing}
                        label="Has Premium Editing?"
                        updateOnChange={patchTaskContext}
                        disabled={(itemInfo && itemInfo.state === "completed") || props.isCompleted}
                    />
                </div>
                <div className="column -span10">
                    {isCostcoConfigActive && productFeature && (
                        <NotificationBar
                            className="m-l-32"
                            variant="small"
                            type="info"
                        >
                            {productFeature.name}
                        </NotificationBar>
                    )}
                    <div className="notification-header button-group">
                        {isSampleUpc && (
                            <NotificationBar
                                message={sampleProductText}
                                type="info"
                            />
                        )}
                        <TaskMessages message={messaging && messaging.scheduling ? messaging.scheduling : null} />
                    </div>
                    <div className="txt-right">
                        {itemInfo && itemInfo.taskUpdatedBy && (
                            <label className="text-inner-status">
                                <b>Last Updated By: </b> {itemInfo.taskUpdatedBy}
                            </label>
                        )}
                    </div>
                    {!_.isEmpty(definitions) && (
                        <AttributeErrors
                            definitions={[...definitions, ...invalidGroupAttributes]}
                            codingWipKey={codingWipKey}
                            showValidation={isProductInvalid ? isProductInvalid : props.showValidation}
                        />
                    )}
                    <div className="row">
                        <ProfilingMainAttributes
                            upc={upc}
                            productModifier={productModifier}
                            codingWipKey={codingWipKey}
                            definitions={definitions}
                            itemInfo={itemInfo}
                            profilingTaskKey={profilingTaskKey}
                            isFinalized={isFinalized}
                            isTaskCompleted={itemInfo?.isCompleted}
                            taskType={taskType}
                            onRefetchDefinitions={refetchDefinitions}
                            onValidateGroupAttributes={onValidateGroupAttributes}
                            readOnlyAdd={props.readOnlyAdd}
                            readOnlyEdit={props.readOnlyEdit}
                            hasMissingSizeAttributes={hasMissingSizeAttributes}
                            showMissingSizeAttributes={showMissingSizeAttributes}
                        />
                        <div className="column -span6">
                            <div className="height-wrapper">
                                <Notes
                                    settings={{
                                        shipmentKey: shipmentKey,
                                        shipmentProductKey: shipmentProductKey,
                                    }}
                                    clientCode={cxHClientCode}
                                    binTrackingNumber={[binTrackingNumber]}
                                    noteType="Product"
                                    taskType="scheduling.default"
                                    sourcePage={SourcePage[taskType]}
                                >
                                    <NoteDisplay
                                        taskType="scheduling.default"
                                        maxHeight={true}
                                        isReadOnly={props.readOnlyNotes || props.isCompleted}
                                    />
                                </Notes>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="bulk">{includeBulk && !props.readOnlyEdit && <BulkCheck codingWipKey={codingWipKey} />}</div>
        </div>
    );
};

export default Product;
